import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Send, Loader2 } from 'lucide-react'
import { useAuthStore } from '@/store/useAuthStore'
import { useUIStore } from '@/store/uiStore'
import { startConversation } from '@/services/api/conversations'
import { Button } from '@/components/common/Button'
import { formatPrice } from '@/utils/format'

interface MessageModalProps {
  isOpen: boolean
  onClose: () => void
  data?: {
    listingId: string
    sellerId: string
    sellerName: string
    listingTitle: string
    listingImage?: string
    listingPrice?: number
    listingType?: string
    // 'contact-buyer' flips the copy for a seller messaging a buyer (e.g. an
    // auction winner). sellerId/sellerName then hold the recipient (the buyer).
    mode?: 'contact-seller' | 'contact-buyer'
  }
}

const QUICK_MESSAGES = [
  'Hi, is this still available?',
  'What is your best price?',
  'Can I arrange a viewing?',
  'Is the price negotiable?',
]

const VEHICLE_QUICK_MESSAGES = [
  'Is this vehicle still available?',
  'Can I arrange a test drive?',
  "I'm interested in a trade-in",
  'What finance options are available?',
  'Can you send more photos?',
]

const FUNDRAISER_QUICK_MESSAGES = [
  'Hi, I have a question about this fundraiser',
  'How can I get involved?',
  'Is this fundraiser still active?',
]

// Seller → buyer (e.g. contacting an auction winner).
const SELLER_TO_BUYER_QUICK_MESSAGES = [
  'Congratulations! Here are the next steps to complete your order.',
  'Thanks for your purchase! When would suit for pickup or delivery?',
  'Just a reminder to complete payment so I can get this to you.',
  'Let me know if you have any questions about your order.',
]

export function MessageModal({ isOpen, onClose, data }: MessageModalProps) {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { showToast } = useUIStore()
  const [message, setMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  // Focus trap
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !dialogRef.current) return
    const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }, [])

  // Auto-focus textarea when opened
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      const textarea = dialogRef.current.querySelector('textarea')
      if (textarea) textarea.focus()
    }
  }, [isOpen])

  if (!isOpen || !data) return null

  const handleSend = async () => {
    if (!user || !message.trim()) return

    setIsSending(true)
    try {
      const conversationId = await startConversation({
        senderId: user.uid,
        senderName: user.displayName || 'Anonymous',
        recipientId: data.sellerId,
        recipientName: data.sellerName,
        listingId: data.listingId,
        listingTitle: data.listingTitle,
        listingImage: data.listingImage,
        listingPrice: data.listingPrice,
        message: message.trim(),
      })

      showToast('Message sent!', 'success')
      onClose()
      navigate(`/messages/${conversationId}`)
    } catch (error) {
      console.error('Error sending message:', error)
      showToast('Failed to send message. Please try again.', 'error')
    } finally {
      setIsSending(false)
    }
  }

  const handleQuickMessage = (text: string) => {
    setMessage(text)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="message-modal-title"
        onKeyDown={handleKeyDown}
        className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 id="message-modal-title" className="text-lg font-semibold">{data?.mode === 'contact-buyer' ? 'Message Buyer' : data?.listingType === 'fundraiser' ? 'Contact Fundraiser' : 'Contact Seller'}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            aria-label="Close message dialog"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Listing preview */}
        <div className="p-4 bg-slate-50 border-b border-slate-200">
          <div className="flex gap-3">
            {data.listingImage ? (
              <img
                src={data.listingImage}
                alt={data.listingTitle}
                className="w-16 h-16 rounded-lg object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-slate-200 flex items-center justify-center text-2xl">
                📷
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-slate-900 truncate">
                {data.listingTitle}
              </h3>
              {data.listingPrice !== undefined && (
                <p className="text-primary-600 font-semibold">
                  {formatPrice(data.listingPrice)}
                </p>
              )}
              <p className="text-sm text-slate-500">{data.mode === 'contact-buyer' ? 'Buyer' : 'Seller'}: {data.sellerName}</p>
            </div>
          </div>
        </div>

        {/* Quick messages */}
        <div className="p-4 border-b border-slate-200">
          <p className="text-sm text-slate-500 mb-2">Quick messages:</p>
          <div className="flex flex-wrap gap-2">
            {(data.mode === 'contact-buyer'
              ? SELLER_TO_BUYER_QUICK_MESSAGES
              : data.listingType === 'vehicle'
                ? VEHICLE_QUICK_MESSAGES
                : data.listingType === 'fundraiser'
                  ? FUNDRAISER_QUICK_MESSAGES
                  : QUICK_MESSAGES
            ).map((text) => (
              <button
                key={text}
                onClick={() => handleQuickMessage(text)}
                className="px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 rounded-full transition-colors"
              >
                {text}
              </button>
            ))}
          </div>
        </div>

        {/* Message input */}
        <div className="p-4">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write your message..."
            aria-label="Message to seller"
            className="input min-h-[120px] resize-none"
            maxLength={5000}
            disabled={isSending}
          />
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-slate-200">
          <Button
            variant="secondary"
            onClick={onClose}
            className="flex-1"
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            className="flex-1"
            disabled={!message.trim() || isSending}
          >
            {isSending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Message
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
